import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useLatestRef } from '@gonfalon/react/useLatestRef';

interface UseQuickSubmitOptions {
  onSubmit?: () => void;
  isDisabled?: boolean;
  scope?: 'event' | 'document';
}

interface QuickSubmitKeyboardEvent {
  ctrlKey: boolean;
  key: string;
  metaKey: boolean;
  target: EventTarget | null;
  currentTarget?: EventTarget | null;
  preventDefault: () => void;
}

function isQuickSubmitKeyboardEvent(event: QuickSubmitKeyboardEvent) {
  return (event.metaKey || event.ctrlKey) && event.key === 'Enter';
}

function isSubmitButton(element: HTMLButtonElement) {
  return element.type === 'submit';
}

function getQuickSubmitElement(target: EventTarget | null): HTMLTextAreaElement | HTMLButtonElement | null {
  if (!(target instanceof HTMLElement)) {
    return null;
  }
  if (target instanceof HTMLTextAreaElement) {
    return target;
  }
  if (target instanceof HTMLButtonElement && isSubmitButton(target)) {
    return target;
  }
  const button = target.closest('button');
  return button instanceof HTMLButtonElement && isSubmitButton(button) ? button : null;
}

function requestQuickSubmit(element: HTMLTextAreaElement | HTMLButtonElement) {
  const form = element.form ?? element.closest('form');
  if (!form) {
    return;
  }
  if (element instanceof HTMLButtonElement) {
    form.requestSubmit(element);
    return;
  }
  form.requestSubmit();
}

/** Submit a form or custom handler when the user presses `⌘ + Enter` or `Ctrl + Enter`. Attach `props` to a textarea or submit button, or use document scope with `onSubmit` for non-form submit flows. */
export function useQuickSubmit(options: UseQuickSubmitOptions = {}) {
  const scope = options.scope ?? 'event';
  const stateRef = useLatestRef({
    isDisabled: options.isDisabled ?? false,
    onSubmit: options.onSubmit,
  });

  const handleQuickSubmit = useCallback((event: QuickSubmitKeyboardEvent) => {
    if (!isQuickSubmitKeyboardEvent(event) || stateRef.current.isDisabled) {
      return;
    }

    const onSubmit = stateRef.current.onSubmit;
    const quickSubmitElement = onSubmit
      ? null
      : (getQuickSubmitElement(event.target) ?? getQuickSubmitElement(event.currentTarget ?? null));

    if (!onSubmit && !quickSubmitElement) {
      return;
    }

    event.preventDefault();

    if (onSubmit) {
      onSubmit();
      return;
    }

    if (quickSubmitElement) {
      requestQuickSubmit(quickSubmitElement);
    }
  }, []);

  useEffect(() => {
    if (scope !== 'document') {
      return;
    }
    document.addEventListener('keydown', handleQuickSubmit);
    return () => document.removeEventListener('keydown', handleQuickSubmit);
  }, [handleQuickSubmit, scope]);

  return useMemo(
    () => ({ props: { onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => handleQuickSubmit(event) } }),
    [handleQuickSubmit],
  );
}
